import React, { useEffect, useState } from "react";
import { Modal, Button, message, Checkbox, ConfigProvider } from "antd";
import axios from "axios";
import "./ReBillPopupModal.scss";
import { baseUrl } from "../../utils/axiosInstance";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { FormatUSD } from "../../utils/helpers/string";
import { useSelector } from "react-redux";

dayjs.extend(utc);

const ReBillPopupModal = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rebillSelections, setRebillSelections] = useState([]);
  const [modalClosed, setModalClosed] = useState(false);

  const idToken = useSelector((state) => state.auth.idToken);

  const authHeaders = {
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
  };

  useEffect(() => {
    if (modalClosed) return;

    const fetchExpiringAccounts = async () => {
      try {
        const response = await axios.get(
          `${baseUrl}account/user-account-names/`,
          authHeaders
        );
        const tomorrowUTC = dayjs.utc().add(1, "day").format("YYYY-MM-DD");

        const expiringAccounts = response.data.filter((account) => {
          const expiryUTC = dayjs.utc(account.expiry_date).format("YYYY-MM-DD");

          return (
            expiryUTC === tomorrowUTC &&
            account.is_renew_at_rebill === null &&
            account?.is_sub_active === true &&
            !["failed", "in_active", "passed"].includes(account?.status)
          );
        });

        if (expiringAccounts.length > 0) {
          setRebillSelections(expiringAccounts);
          setIsModalVisible(true);
        }
      } catch (err) {
        console.error("Error fetching expiring accounts", err);
      }
    };

    fetchExpiringAccounts();
  }, [modalClosed]);

  const handleChange = (loginId, value) => {
    setRebillSelections((prev) =>
      prev.map((item) =>
        item.value === loginId ? { ...item, is_renew_at_rebill: value } : item
      )
    );
  };

  const handleSubmit = async () => {
    const incomplete = rebillSelections.some(
      (item) => item.is_renew_at_rebill === null
    );
    if (incomplete) {
      message.warning("Please make a selection (Reset or Continue) for each account.");
      return;
    }

    setLoading(true);
    try {
      const payload = rebillSelections.map(({ value, is_renew_at_rebill }) => ({
        value,
        is_renew_at_rebill,
      }));

      await axios.post(
        `${baseUrl}account/change-rebill-status/`,
        payload.map((item)=>({login_id:item.value,is_rebill:item?.is_renew_at_rebill})),
        authHeaders
      );

      message.success("Rebill statuses updated successfully.");
      setIsModalVisible(false);
      setModalClosed(true);
    } catch (error) {
      console.error(error);
      message.error("Failed to update rebill statuses.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelModal = () => {
    setIsModalVisible(false);
    setModalClosed(true);
  };

  return (
    <Modal
      title="Rebill Confirmation"
      className="ReBillPopupModal"
      visible={isModalVisible}
      onCancel={handleCancelModal}
      centered
      footer={[
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={handleSubmit}
        >
          Submit
        </Button>,
      ]}
    >
      {rebillSelections.length > 0 ? (
        <>
          <div className="modal-description">
            <p>Account Rebills Scheduled for Tomorrow</p>
            <p>Would you like to reset or continue at the current level?</p>
          </div>
          {rebillSelections.map((account) => (
            <div key={account.value} className="login_id_div">
              <p>
                <strong>Account Name:</strong> {account.label || "N/A"} &nbsp;
                <strong>Balance:</strong> {FormatUSD(account.balance || 0)}
              </p>
              <div className="terms_checkbox confirmation_msg">
                <ConfigProvider
                  theme={{ token: { colorPrimaryHover: "none" } }}
                >
                  <div className="checkbox-group">
                    <Checkbox
                      checked={account.is_renew_at_rebill === true}
                      onChange={(e) =>
                        handleChange(
                          account.value,
                          e.target.checked ? true : null
                        )
                      }
                    >
                      Reset
                    </Checkbox>
                    <Checkbox
                      checked={account.is_renew_at_rebill === false}
                      onChange={(e) =>
                        handleChange(
                          account.value,
                          e.target.checked ? false : null
                        )
                      }
                    >
                      Continue at current level
                    </Checkbox>
                  </div>
                </ConfigProvider>
              </div>
            </div>
          ))}
        </>
      ) : (
        <p>No expiring accounts found.</p>
      )}
    </Modal>
  );
};

export default ReBillPopupModal;
